<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="700">
        <v-card>
            <v-card-title id="heading" class="d-flex justify-space-between">
                <h4>Alert Details</h4>
                <v-btn density="compact" icon="mdi-close" variant="flat" @click="$emit('update:modelValue',false)"/>
            </v-card-title>

            <v-container>
                <v-row>
                    <v-col cols="12">
                        <v-table>
                            <tbody v-if="!loading && alertDetails">
                            <tr>
                                <td class="font-weight-bold">Alert ID</td>
                                <td>{{ alertDetails.id }}</td>
                                <td class="font-weight-bold">Alert Type</td>
                                <td>{{ alertLabel(alertDetails) }}</td>
                            </tr>
                            <tr>
                                <td class="font-weight-bold">Asset</td>
                                <td>{{ alert.asset }}</td>
                                <td class="font-weight-bold">Device</td>
                                <td>{{ alertDetails.slno }}</td>
                            </tr>
                            <tr>
                                <td class="font-weight-bold">Monitoring Config</td>
                                <td>{{ alertDetails.monitoring_config }}</td>
                                <td class="font-weight-bold">Event Timestamp</td>
                                <td>{{ formatDateTime(alertDetails.event_timestamp) }}</td>
                            </tr>
                            <tr>
                                <td>
                                    <b class="me-2">APP:</b>
                                    <v-icon :icon="`mdi-${alertDetails.app?'check':'close'}-circle`" size="small"/>
                                </td>
                                <td>
                                    <b class="me-2">SMS:</b>
                                    <v-icon :icon="`mdi-${alertDetails.sms?'check':'close'}-circle`" size="small"/>
                                </td>
                                <td colspan="2">
                                    <b class="me-2">EMAIL:</b>
                                    <v-icon :icon="`mdi-${alertDetails.email?'check':'close'}-circle`" size="small"/>
                                </td>
                            </tr>
                            <tr>
                                <td class="font-weight-bold">Payload</td>
                                <td colspan="3">
                                    <v-table density="compact" class="payload-table">
                                        <tbody>
                                            <!-- Speed Alert -->
                                            <template v-if="alertDetails.alert_type === ALERT_TYPE.SPEED && alertDetails.payload">
                                                <tr v-if="alertDetails.payload.speed_threshold">
                                                    <td class="font-weight-bold" width="140">
                                                        <v-icon icon="mdi-speedometer-slow" size="small" class="me-1"/>Speed Limit
                                                    </td>
                                                    <td>{{ strOperations().unitifyDistance(alertDetails.payload.speed_threshold) }}</td>
                                                </tr>
                                                <tr v-if="alertDetails.payload.current_speed || alertDetails.payload.speed">
                                                    <td class="font-weight-bold">
                                                        <v-icon icon="mdi-speedometer" size="small" class="me-1" color="red"/>Actual Speed
                                                    </td>
                                                    <td class="text-red font-weight-bold">{{ strOperations().unitifyDistance(alertDetails.payload.current_speed || alertDetails.payload.speed) }}</td>
                                                </tr>
                                            </template>

                                            <!-- Geofence Alert -->
                                            <template v-else-if="alertDetails.alert_type === ALERT_TYPE.GEOFENCE && alertDetails.payload">
                                                <tr v-if="alertDetails.payload.template_label">
                                                    <td class="font-weight-bold" width="140">
                                                        <v-icon icon="mdi-fence" size="small" class="me-1" color="orange"/>Zone
                                                    </td>
                                                    <td>{{ alertDetails.payload.template_label }}</td>
                                                </tr>
                                                <tr v-if="direction">
                                                    <td class="font-weight-bold">
                                                        <v-icon :icon="direction === 'enter' ? 'mdi-login' : 'mdi-logout'" size="small" class="me-1"
                                                                :color="direction === 'enter' ? 'green' : 'red'"/>
                                                        Action
                                                    </td>
                                                    <td :class="direction === 'enter' ? 'text-green' : 'text-red'" class="font-weight-bold">
                                                        {{ direction === 'enter' ? 'Entered' : 'Exited' }}
                                                    </td>
                                                </tr>
                                            </template>

                                            <!-- No-show, place, departure and arrival (9-12) -->
                                            <template v-else-if="isPlaceFamily && alertDetails.payload">
                                                <tr v-if="placeName">
                                                    <td class="font-weight-bold" width="140">
                                                        <v-icon icon="mdi-map-marker" size="small" class="me-1" :color="alertColor(alertDetails)"/>
                                                        {{ detailsType === ALERT_TYPE.NO_SHOW ? 'Expected At' : 'Place' }}
                                                    </td>
                                                    <td>{{ placeName }}</td>
                                                </tr>
                                                <tr v-if="detailsType === ALERT_TYPE.NO_SHOW">
                                                    <td class="font-weight-bold" width="140">
                                                        <v-icon icon="mdi-alert-circle-outline" size="small" class="me-1" :color="alertColor(alertDetails)"/>Status
                                                    </td>
                                                    <td class="font-weight-bold">Did Not Arrive</td>
                                                </tr>
                                                <tr v-if="detailsType === ALERT_TYPE.NO_SHOW && alertDetails.payload.arrive_by">
                                                    <td class="font-weight-bold" width="140">
                                                        <v-icon icon="mdi-clock-outline" size="small" class="me-1" :color="alertColor(alertDetails)"/>Arrive By
                                                    </td>
                                                    <td>{{ alertDetails.payload.arrive_by }}</td>
                                                </tr>
                                                <tr v-else-if="placeDirection">
                                                    <td class="font-weight-bold" width="140">
                                                        <v-icon :icon="placeDirection === 'enter' ? 'mdi-login' : 'mdi-logout'" size="small" class="me-1"
                                                                :color="placeDirection === 'enter' ? 'green' : 'red'"/>
                                                        Direction
                                                    </td>
                                                    <td :class="placeDirection === 'enter' ? 'text-green' : 'text-red'" class="font-weight-bold">
                                                        {{ placeDirection === 'enter' ? 'Arrived' : 'Left' }}
                                                    </td>
                                                </tr>
                                            </template>

                                            <!-- Temperature Alert -->
                                            <template v-else-if="alertDetails.alert_type === ALERT_TYPE.TEMPERATURE && alertDetails.payload">
                                                <tr v-if="alertDetails.payload.current_temperature != null">
                                                    <td class="font-weight-bold" width="140">
                                                        <v-icon icon="mdi-thermometer" size="small" class="me-1" color="red"/>Current
                                                    </td>
                                                    <td class="font-weight-bold">{{ alertDetails.payload.current_temperature }}&deg;</td>
                                                </tr>
                                                <tr v-if="alertDetails.payload.low_threshold_celsius != null">
                                                    <td class="font-weight-bold">
                                                        <v-icon icon="mdi-thermometer-low" size="small" class="me-1" color="blue"/>Low Threshold
                                                    </td>
                                                    <td>{{ alertDetails.payload.low_threshold_celsius }}&deg;C</td>
                                                </tr>
                                                <tr v-if="alertDetails.payload.high_threshold_celsius != null">
                                                    <td class="font-weight-bold">
                                                        <v-icon icon="mdi-thermometer-high" size="small" class="me-1" color="red"/>High Threshold
                                                    </td>
                                                    <td>{{ alertDetails.payload.high_threshold_celsius }}&deg;C</td>
                                                </tr>
                                            </template>

                                            <!-- Light Alert -->
                                            <template v-else-if="alertDetails.alert_type === ALERT_TYPE.LIGHT && alertDetails.payload">
                                                <tr v-if="alertDetails.payload.lux != null">
                                                    <td class="font-weight-bold" width="140">
                                                        <v-icon icon="mdi-brightness-6" size="small" class="me-1" color="amber"/>Light Level
                                                    </td>
                                                    <td>{{ alertDetails.payload.lux }} lux</td>
                                                </tr>
                                                <tr v-if="alertDetails.payload.lux_int_type">
                                                    <td class="font-weight-bold">
                                                        <v-icon icon="mdi-swap-horizontal" size="small" class="me-1"/>Change
                                                    </td>
                                                    <td>{{ formatLuxType(alertDetails.payload.lux_int_type) }}</td>
                                                </tr>
                                            </template>

                                            <!-- Push Button Alert -->
                                            <template v-else-if="alertDetails.alert_type === ALERT_TYPE.PUSH_BUTTON && alertDetails.payload">
                                                <tr>
                                                    <td class="font-weight-bold" width="140">
                                                        <v-icon icon="mdi-gesture-tap-button" size="small" class="me-1" color="purple"/>Status
                                                    </td>
                                                    <td>Button Pressed</td>
                                                </tr>
                                            </template>

                                            <!-- System Alert (Battery, Crash, Maintenance) -->
                                            <template v-else-if="alertDetails.payload && (alertDetails.payload.type === SYSTEM_ALERTS_TYPE || alertDetails.payload.sub_type || SYSTEM_ALERT_TYPES.includes(alertDetails.alert_type))">
                                                <tr v-if="alertDetails.payload.sub_type">
                                                    <td class="font-weight-bold" width="140">
                                                        <v-icon icon="mdi-information" size="small" class="me-1"/>Type
                                                    </td>
                                                    <td>{{ formatSubType(alertDetails.payload.sub_type) }}</td>
                                                </tr>
                                                <tr v-if="primaryBattery">
                                                    <td class="font-weight-bold">
                                                        <v-icon icon="mdi-flash" size="small" class="me-1" color="orange"/>{{ primaryBatteryLabel }}
                                                    </td>
                                                    <td>{{ primaryBattery }}</td>
                                                </tr>
                                                <tr v-if="secondaryVoltage">
                                                    <td class="font-weight-bold">
                                                        <v-icon icon="mdi-flash-outline" size="small" class="me-1" color="orange"/>{{ secondaryBatteryLabel }}
                                                    </td>
                                                    <td>{{ (secondaryVoltage / 1000).toFixed(3) }}v</td>
                                                </tr>
                                                <tr v-if="alertDetails.payload.pending_dtcs">
                                                    <td class="font-weight-bold">
                                                        <v-icon icon="mdi-alert-circle" size="small" class="me-1" color="orange"/>Pending DTCs
                                                    </td>
                                                    <td>{{ alertDetails.payload.pending_dtcs }}</td>
                                                </tr>
                                                <tr v-if="alertDetails.payload.indicator_status">
                                                    <td class="font-weight-bold">
                                                        <v-icon icon="mdi-engine" size="small" class="me-1" color="amber"/>MIL Status
                                                    </td>
                                                    <td>{{ alertDetails.payload.indicator_status }}</td>
                                                </tr>
                                                <tr v-if="alertDetails.payload.red_stop_lamp === 1">
                                                    <td class="font-weight-bold">
                                                        <v-icon icon="mdi-alert-octagon" size="small" class="me-1" color="error"/>Red Stop Lamp
                                                    </td>
                                                    <td class="font-weight-bold text-error">On</td>
                                                </tr>
                                                <tr v-if="alertDetails.payload.amber_warning_lamp === 1">
                                                    <td class="font-weight-bold">
                                                        <v-icon icon="mdi-alert" size="small" class="me-1" color="amber"/>Amber Warning Lamp
                                                    </td>
                                                    <td>On</td>
                                                </tr>
                                                <tr v-if="alertDetails.payload.protect_lamp === 1">
                                                    <td class="font-weight-bold">
                                                        <v-icon icon="mdi-shield-alert" size="small" class="me-1" color="amber"/>Protect Lamp
                                                    </td>
                                                    <td>On</td>
                                                </tr>
                                            </template>

                                            <!-- Fallback -->
                                            <template v-else-if="alertDetails.payload">
                                                <tr v-for="(val, key) in payloadDisplayFields" :key="key">
                                                    <td class="font-weight-bold" width="140">{{ key }}</td>
                                                    <td>{{ val }}</td>
                                                </tr>
                                            </template>

                                            <!-- Location (shown for any type that has coordinates) -->
                                            <template v-if="coords">
                                                <tr>
                                                    <td class="font-weight-bold">
                                                        <v-icon icon="mdi-crosshairs-gps" size="small" class="me-1" color="green"/>Location
                                                    </td>
                                                    <td class="d-flex align-center ga-1">
                                                        <span>{{ coords.lat }}, {{ coords.lng }}</span>
                                                        <v-tooltip location="top" text="Open in Google Maps">
                                                            <template v-slot:activator="{ props: tp }">
                                                                <v-btn :href="`https://www.google.com/maps?q=${coords.lat},${coords.lng}`"
                                                                       target="_blank" icon="mdi-google-maps" size="x-small" variant="text"
                                                                       color="red" density="compact" v-bind="tp"/>
                                                            </template>
                                                        </v-tooltip>
                                                        <v-tooltip location="top" text="Copy coordinates">
                                                            <template v-slot:activator="{ props: tp }">
                                                                <v-btn icon="mdi-content-copy" size="x-small" variant="text"
                                                                       color="grey" density="compact" v-bind="tp"
                                                                       @click="copyCoords(coords.lat, coords.lng)"/>
                                                            </template>
                                                        </v-tooltip>
                                                    </td>
                                                </tr>
                                                <tr v-if="alertDetails.payload.location">
                                                    <td class="font-weight-bold">
                                                        <v-icon icon="mdi-map-search" size="small" class="me-1"/>Address
                                                    </td>
                                                    <td>{{ alertDetails.payload.location }}</td>
                                                </tr>
                                            </template>
                                        </tbody>
                                    </v-table>
                                </td>
                            </tr>
                            </tbody>
                            <tbody v-else>
                            <tr class="text-center">
                                <td v-if="loading" colspan="9">
                                    <v-progress-circular color="primary" indeterminate/>
                                </td>
                                <td v-else colspan="9">No record found</td>
                            </tr>
                            </tbody>
                        </v-table>
                    </v-col>
                </v-row>
            </v-container>

            <v-card-actions class="px-4 d-flex justify-end">
                <v-btn size="small" type="button" @click="$emit('update:modelValue',false)">
                    Close
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script setup>
import {ALERT_TYPE, SYSTEM_ALERT_TYPES, SYSTEM_ALERTS_TYPE, subTypeLabel, alertLabel, alertColor,
    alertTypeOf, enterOrExitOf, payloadLatLng} from '@/composables/alertTypes';
import {ref, computed, onMounted} from "vue";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {useClipboard} from "@/composables/clipboard";
import {strOperations} from "@/composables/globals";
import {formatDateTime} from "@/composables/useTimezone";

const alertDetails = ref(null);
const snackbar = useSnackbarStore();
const {copyToClipboard} = useClipboard();

const copyCoords = (lat, lng) => {
    copyToClipboard(`${lat}, ${lng}`);
};
const loading = ref(true);
const props = defineProps({
    modelValue: Boolean,
    alert: {
        type: Object
    }
});
const emits = defineEmits(['update:modelValue']);
const dialogSwitch = ref(props.modelValue);

onMounted(() => {
    send('get', `assets/alerts/${props.alert.id}`, {notification: true})
        .then((response) => {
            alertDetails.value = response.data.data.asset_alert;
        })
        .catch((error) => {
            console.log(error)
            snackbar.showSnackbar('Asset alerts could not be loaded', 'error')
        }).finally(() => loading.value = false)
});

const detailsType = computed(() => alertTypeOf(alertDetails.value));
const direction = computed(() => enterOrExitOf(alertDetails.value?.payload));
const coords = computed(() => payloadLatLng(alertDetails.value?.payload));

// 11 and 12 are disabled server side but old rows may still exist.
const PLACE_FAMILY_TYPES = [ALERT_TYPE.NO_SHOW, ALERT_TYPE.PLACE, ALERT_TYPE.DEPARTURE, ALERT_TYPE.ARRIVAL];
const isPlaceFamily = computed(() => PLACE_FAMILY_TYPES.includes(detailsType.value));
const placeName = computed(() => {
    const p = alertDetails.value?.payload;
    return p?.place_name || p?.geofence_name || p?.location_name || p?.template_label || null;
});
// Departure and arrival fix the direction by type; a place alert carries it.
const placeDirection = computed(() => {
    if (detailsType.value === ALERT_TYPE.DEPARTURE) return 'exit';
    if (detailsType.value === ALERT_TYPE.ARRIVAL) return 'enter';
    return direction.value;
});

const PAYLOAD_SKIP_KEYS = new Set(['latitude', 'longitude', 'lat', 'lng', 'location', 'type', 'alert_type',
    'place_name', 'geofence_name', 'location_name',
    'speed_threshold', 'current_speed', 'speed', 'template_label', 'enter_or_exit', 'radius',
    'current_temperature', 'low_threshold_celsius', 'high_threshold_celsius',
    'lux', 'lux_int_type', 'sub_type', 'voltage1', 'voltage2', 'pending_dtcs', 'indicator_status',
    'battery1_status', 'battery2_status', 'battery1_label', 'battery2_label', 'is_phone',
]);

const PAYLOAD_LABELS = {
    device_slno: 'Device Serial', slno: 'Serial No', asset_id: 'Asset ID',
    company_id: 'Company', monitoring_config_id: 'Config ID', trip_id: 'Trip ID',
    event_time: 'Event Time', timestamp: 'Timestamp', speed: 'Speed',
};

const payloadDisplayFields = computed(() => {
    const p = alertDetails.value?.payload;
    if (!p || typeof p !== 'object') return {};
    const result = {};
    for (const [k, v] of Object.entries(p)) {
        if (PAYLOAD_SKIP_KEYS.has(k) || v === null || v === undefined || v === '') continue;
        const label = PAYLOAD_LABELS[k] || k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        result[label] = typeof v === 'object' ? JSON.stringify(v) : v;
    }
    return result;
});

// battery1_status/battery2_status SELECT the reported channel, they are not
// "this cell exists" flags. On an Orion OBD battery2 is the 12V rail and voltage1
// is the 2.8V supercap, so showing voltage1 as the battery was wrong. Phones put a
// percent on voltage1 (percent = voltage1 / 50).
const alertPayload = computed(() => alertDetails.value?.payload || null);
const alertIsPhone = computed(() => alertPayload.value?.is_phone === true || alertPayload.value?.is_phone === 1);
const usesBattery2 = computed(
    () => alertPayload.value?.battery2_status === true && alertPayload.value?.battery1_status !== true
);

const primaryVoltage = computed(() => {
    const p = alertPayload.value;
    if (!p) return null;
    const mv = (!alertIsPhone.value && usesBattery2.value) ? p.voltage2 : p.voltage1;
    return (mv === null || mv === undefined || isNaN(mv)) ? null : Number(mv);
});

const secondaryVoltage = computed(() => {
    const p = alertPayload.value;
    if (!p || alertIsPhone.value) return null;
    const mv = usesBattery2.value ? p.voltage1 : p.voltage2;
    return (mv === null || mv === undefined || isNaN(mv)) ? null : Number(mv);
});

const primaryBattery = computed(() => {
    const mv = primaryVoltage.value;
    if (mv === null) return null;
    if (alertIsPhone.value) return `${Math.max(0, Math.min(100, Math.round(mv / 50)))}%`;
    return `${(mv / 1000).toFixed(3)}v`;
});

// The SKU names its own channels, nullable so keep the old generic wording as a fallback.
const channelLabel = (channel2) => {
    const raw = alertPayload.value?.[channel2 ? 'battery2_label' : 'battery1_label'];
    const s = (raw ?? '').toString().trim();
    return s === '' ? null : s;
};

const primaryBatteryLabel = computed(() => {
    if (alertIsPhone.value) return channelLabel(false) || 'Battery';
    return channelLabel(usesBattery2.value) || (usesBattery2.value ? 'Voltage 2' : 'Voltage 1');
});

const secondaryBatteryLabel = computed(
    () => channelLabel(!usesBattery2.value) || (usesBattery2.value ? 'Voltage 1' : 'Voltage 2')
);

const formatSubType = (subType) => {
    return subTypeLabel(subType);
};

const formatLuxType = (type) => {
    if (!type) return '';
    const types = {
        'light_to_dark': 'Light to Dark',
        'dark_to_light': 'Dark to Light',
    };
    return types[type] || type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

</script>

<style scoped>
.payload-table {
    background: #f5f5f5;
    border-radius: 4px;
}
.payload-table td {
    padding: 6px 12px !important;
}

</style>
